import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileSpreadsheet, Upload, CheckCircle2, AlertCircle, Clock, 
  ChevronRight, Trash2, Eye, FileCheck, XCircle, Search,
  RefreshCw, Download, Filter, BarChart3, AlertTriangle
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { auditoriaExpedientesAPI } from '../api';

// Estado de sesión badges
const ESTADO_BADGES = {
  en_revision: { label: 'En revisión', variant: 'secondary', icon: Clock },
  parcialmente_aplicada: { label: 'Parcialmente aplicada', variant: 'warning', icon: AlertCircle },
  completada: { label: 'Completada', variant: 'success', icon: CheckCircle2 },
  cancelada: { label: 'Cancelada', variant: 'destructive', icon: XCircle },
};

// Estado de item badges
const ITEM_ESTADO_BADGES = {
  pendiente: { label: 'Pendiente', color: 'bg-gray-100 text-gray-700' },
  aprobado: { label: 'Aprobado', color: 'bg-blue-100 text-blue-700' },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-700' },
  aplicado: { label: 'Aplicado', color: 'bg-green-100 text-green-700' },
  error: { label: 'Error', color: 'bg-orange-100 text-orange-700' },
};

export default function AuditoriaExpedientes() {
  const navigate = useNavigate();
  
  // Estado general
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [sesiones, setSesiones] = useState([]);
  const [sesionActual, setSesionActual] = useState(null);
  
  // Estado de subida
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [nombreSesion, setNombreSesion] = useState('');
  const [pestanaExcel, setPestanaExcel] = useState('acabados 2025');
  
  // Estado de sesión detallada
  const [showSesionModal, setShowSesionModal] = useState(false);
  const [itemsSeleccionados, setItemsSeleccionados] = useState([]);
  const [filtroItems, setFiltroItems] = useState('todos');
  const [aplicando, setAplicando] = useState(false);
  
  // Estado de paginación
  const [pagosPage, setPagosPage] = useState(1);
  const [costasPage, setCostasPage] = useState(1);
  const pageSize = 100;
  
  // Estado de pestaña activa
  const [activeTab, setActiveTab] = useState("sesiones");
  
  // Estado de pestaña activa
  
  // Cargar dashboard y sesiones
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, sesionesRes] = await Promise.all([
        auditoriaExpedientesAPI.getDashboard({ pagos_page: pagosPage, costas_page: costasPage, page_size: pageSize }),
        auditoriaExpedientesAPI.getSesiones()
      ]);
      setDashboard(dashRes.data);
      setSesiones(sesionesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos de auditoría');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [pagosPage, costasPage]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Subir Excel
  const handleUploadExcel = async () => {
    if (!uploadFile) {
      toast.error('Selecciona un archivo Excel');
      return;
    }
    
    try {
      setUploading(true);
      const response = await auditoriaExpedientesAPI.subirExcel(
        uploadFile,
        nombreSesion || null,
        pestanaExcel
      );
      
      if (response.data.error) {
        toast.error(response.data.message);
        return;
      }
      
      toast.success(`Excel analizado: ${response.data.resumen.expedientes_encontrados} expedientes encontrados`);
      setShowUploadModal(false);
      setUploadFile(null);
      setNombreSesion('');
      
      // Recargar datos
      loadData();
      
      // Abrir sesión recién creada
      const sesionRes = await auditoriaExpedientesAPI.getSesion(response.data.sesion_id);
      setSesionActual(sesionRes.data);
      setShowSesionModal(true);
      
    } catch (error) {
      toast.error('Error al procesar el Excel');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };
  
  // Abrir sesión existente
  const handleOpenSesion = async (sesionId) => {
    try {
      const response = await auditoriaExpedientesAPI.getSesion(sesionId);
      setSesionActual(response.data);
      setItemsSeleccionados([]);
      setShowSesionModal(true);
    } catch (error) {
      toast.error('Error al cargar la sesión');
    }
  };
  
  // Eliminar sesión
  const handleDeleteSesion = async (sesionId) => {
    if (!window.confirm('¿Eliminar esta sesión de auditoría?')) return;
    
    try {
      await auditoriaExpedientesAPI.deleteSesion(sesionId);
      toast.success('Sesión eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar la sesión');
    }
  };
  
  // Toggle selección de item
  const toggleItemSelection = (itemId) => {
    setItemsSeleccionados(prev => 
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };
  
  // Seleccionar todos los items visibles
  const selectAllVisible = () => {
    const itemsFiltrados = getItemsFiltrados();
    const ids = itemsFiltrados
      .filter(item => item.expediente_id && item.estado === 'pendiente')
      .map(item => item.id);
    setItemsSeleccionados(ids);
  };
  
  // Deseleccionar todos
  const deselectAll = () => {
    setItemsSeleccionados([]);
  };
  
  // Obtener items filtrados
  const getItemsFiltrados = () => {
    if (!sesionActual?.items) return [];
    
    switch (filtroItems) {
      case 'encontrados':
        return sesionActual.items.filter(item => item.expediente_id);
      case 'no_encontrados':
        return sesionActual.items.filter(item => !item.expediente_id);
      case 'con_diferencias':
        return sesionActual.items.filter(item => item.diferencias?.tiene_diferencias);
      case 'pendientes':
        return sesionActual.items.filter(item => item.estado === 'pendiente');
      case 'aplicados':
        return sesionActual.items.filter(item => item.estado === 'aplicado');
      default:
        return sesionActual.items;
    }
  };
  
  // Aplicar cambios seleccionados
  const handleAplicarCambios = async () => {
    if (itemsSeleccionados.length === 0) {
      toast.error('Selecciona al menos un expediente');
      return;
    }
    
    if (!window.confirm(`¿Aplicar cambios a ${itemsSeleccionados.length} expedientes?`)) {
      return;
    }
    
    try {
      setAplicando(true);
      const response = await auditoriaExpedientesAPI.aplicarItems(sesionActual.id, {
        item_ids: itemsSeleccionados,
        aplicar_clausulas: true,
        aplicar_pagos: true,
        crear_actuacion: true
      });
      
      toast.success(`${response.data.aplicados} expedientes actualizados`);
      
      if (response.data.errores?.length > 0) {
        toast.warning(`${response.data.errores.length} errores encontrados`);
      }
      
      // Recargar sesión
      const sesionRes = await auditoriaExpedientesAPI.getSesion(sesionActual.id);
      setSesionActual(sesionRes.data);
      setItemsSeleccionados([]);
      loadData();
      
    } catch (error) {
      toast.error('Error al aplicar cambios');
      console.error(error);
    } finally {
      setAplicando(false);
    }
  };
  
  // Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };
  
  // Formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Auditoría de Expedientes</h1>
          <p className="text-gray-500">Control y seguimiento mediante importación de Excel</p>
        </div>
        <Button onClick={() => setShowUploadModal(true)} className="gap-2">
          <Upload className="h-4 w-4" />
          Subir Excel
        </Button>
      </div>
      
      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Sesiones activas */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Sesiones Activas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.sesiones_activas?.length || 0}</div>
            <p className="text-xs text-gray-500">Pendientes de completar</p>
          </CardContent>
        </Card>
        
        {/* Pagos pendientes cliente */}
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Pagos Pendientes a Clientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">
              {formatCurrency(dashboard?.pagos_pendientes_cliente?.importe_total)}
            </div>
            <p className="text-xs text-red-600">
              {dashboard?.pagos_pendientes_cliente?.total || 0} expedientes
            </p>
          </CardContent>
        </Card>
        
        {/* Costas pendientes */}
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-orange-700 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Costas Pendientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">
              {formatCurrency(dashboard?.costas_pendientes?.importe_total)}
            </div>
            <p className="text-xs text-orange-600">
              {dashboard?.costas_pendientes?.total || 0} expedientes
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs principales */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="sesiones">Sesiones de Auditoría</TabsTrigger>
          <TabsTrigger value="pagos">Pagos Pendientes</TabsTrigger>
          <TabsTrigger value="costas">Costas Pendientes</TabsTrigger>
        </TabsList>
        
        {/* Tab: Sesiones */}
        <TabsContent value="sesiones">
          <Card>
            <CardHeader>
              <CardTitle>Sesiones de Auditoría</CardTitle>
              <CardDescription>
                Historial de importaciones de Excel y su estado de procesamiento
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sesiones.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay sesiones de auditoría</p>
                  <p className="text-sm">Sube un archivo Excel para comenzar</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sesiones.map(sesion => {
                    const estadoInfo = ESTADO_BADGES[sesion.estado] || ESTADO_BADGES.en_revision;
                    const IconEstado = estadoInfo.icon;
                    
                    return (
                      <div 
                        key={sesion.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-medium">{sesion.nombre}</h3>
                            <p className="text-sm text-gray-500">
                              {sesion.archivo_nombre} • {formatDate(sesion.created_at)}
                            </p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {sesion.total_filas_excel} filas
                              </Badge>
                              <Badge variant="outline" className="text-xs text-green-600">
                                {sesion.total_expedientes_encontrados} encontrados
                              </Badge>
                              {sesion.total_expedientes_no_encontrados > 0 && (
                                <Badge variant="outline" className="text-xs text-red-600">
                                  {sesion.total_expedientes_no_encontrados} no encontrados
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Badge className={`flex items-center gap-1 ${
                            sesion.estado === 'completada' ? 'bg-green-100 text-green-700' :
                            sesion.estado === 'parcialmente_aplicada' ? 'bg-yellow-100 text-yellow-700' :
                            sesion.estado === 'cancelada' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            <IconEstado className="h-3 w-3" />
                            {estadoInfo.label}
                          </Badge>
                          
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleOpenSesion(sesion.id)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Ver
                          </Button>
                          
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteSesion(sesion.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Tab: Pagos Pendientes */}
        <TabsContent value="pagos">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Pagos Pendientes a Clientes
                <Badge variant="outline" className="ml-2">
                  {dashboard?.pagos_pendientes_cliente?.total || 0} total
                </Badge>
              </CardTitle>
              <CardDescription>
                Expedientes con importes pendientes de pago al cliente (Principal + Intereses)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard?.pagos_pendientes_cliente?.expedientes?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-300" />
                  <p>No hay pagos pendientes a clientes</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2 max-h-[2000px] overflow-y-auto">
                    {dashboard?.pagos_pendientes_cliente?.expedientes?.map(exp => (
                      <div 
                        key={exp.expediente_id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-red-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/expedientes/${exp.expediente_id}`)}
                      >
                        <div>
                          <h4 className="font-medium">{exp.numero_expediente}</h4>
                          <p className="text-sm text-gray-500">{exp.cliente_nombre || 'Sin cliente'}</p>
                          {exp.conceptos?.length > 0 && (
                            <p className="text-xs text-gray-400">{exp.conceptos.join(', ')}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-red-600">{formatCurrency(exp.importe_pendiente)}</p>
                          <ChevronRight className="h-4 w-4 text-gray-400 inline" />
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Paginación */}
                  {dashboard?.pagos_pendientes_cliente?.total_pages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-gray-500">
                        Página {dashboard?.pagos_pendientes_cliente?.page} de {dashboard?.pagos_pendientes_cliente?.total_pages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagosPage === 1}
                          onClick={() => setPagosPage(p => Math.max(1, p - 1))}
                        >
                          Anterior
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagosPage >= dashboard?.pagos_pendientes_cliente?.total_pages}
                          onClick={() => setPagosPage(p => p + 1)}
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Tab: Costas Pendientes */}
        <TabsContent value="costas">
          <Card>
            <CardHeader>
              <CardTitle className="text-orange-700 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Costas Pendientes de Cobro
                <Badge variant="outline" className="ml-2">
                  {dashboard?.costas_pendientes?.total || 0} total
                </Badge>
              </CardTitle>
              <CardDescription>
                Expedientes con costas procesales pendientes de cobrar
              </CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard?.costas_pendientes?.expedientes?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-300" />
                  <p>No hay costas pendientes</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2 max-h-[2000px] overflow-y-auto">
                    {dashboard?.costas_pendientes?.expedientes?.map(exp => (
                      <div 
                        key={exp.expediente_id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-orange-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/expedientes/${exp.expediente_id}`)}
                      >
                        <div>
                          <h4 className="font-medium">{exp.numero_expediente}</h4>
                          <p className="text-sm text-gray-500">{exp.cliente_nombre || 'Sin cliente'}</p>
                          {exp.conceptos?.length > 0 && (
                            <p className="text-xs text-gray-400">{exp.conceptos.join(', ')}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-orange-600">{formatCurrency(exp.importe_pendiente)}</p>
                          <ChevronRight className="h-4 w-4 text-gray-400 inline" />
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Paginación */}
                  {dashboard?.costas_pendientes?.total_pages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-gray-500">
                        Página {dashboard?.costas_pendientes?.page} de {dashboard?.costas_pendientes?.total_pages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={costasPage === 1}
                          onClick={() => setCostasPage(p => Math.max(1, p - 1))}
                        >
                          Anterior
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={costasPage >= dashboard?.costas_pendientes?.total_pages}
                          onClick={() => setCostasPage(p => p + 1)}
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Modal: Subir Excel */}
      <Dialog open={showUploadModal} onOpenChange={(open) => !uploading && setShowUploadModal(open)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Subir Excel de Auditoría</DialogTitle>
            <DialogDescription>
              Sube el archivo Excel con los datos de control de expedientes para analizar
            </DialogDescription>
          </DialogHeader>
          
          {uploading ? (
            <div className="py-8 text-center">
              <RefreshCw className="h-12 w-12 mx-auto mb-4 animate-spin text-blue-500" />
              <p className="text-lg font-medium">Analizando Excel...</p>
              <p className="text-sm text-gray-500 mt-2">Esto puede tardar unos segundos</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Archivo Excel</label>
                <Input 
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  disabled={uploading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Nombre de la sesión (opcional)</label>
                <Input 
                  placeholder="Ej: Auditoría Enero 2025"
                  value={nombreSesion}
                  onChange={(e) => setNombreSesion(e.target.value)}
                  disabled={uploading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Pestaña del Excel</label>
                <Input 
                  placeholder="acabados 2025"
                  value={pestanaExcel}
                  onChange={(e) => setPestanaExcel(e.target.value)}
                  disabled={uploading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Indica el nombre de la pestaña a analizar (por defecto: acabados 2025)
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)} disabled={uploading}>
              Cancelar
            </Button>
            <Button onClick={handleUploadExcel} disabled={!uploadFile || uploading}>
              {uploading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Analizar Excel
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal: Detalle de Sesión */}
      <Dialog open={showSesionModal} onOpenChange={setShowSesionModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{sesionActual?.nombre}</span>
              <Badge className={`${
                sesionActual?.estado === 'completada' ? 'bg-green-100 text-green-700' :
                sesionActual?.estado === 'parcialmente_aplicada' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {ESTADO_BADGES[sesionActual?.estado]?.label || sesionActual?.estado}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              {sesionActual?.archivo_nombre} • Pestaña: {sesionActual?.pestana_excel}
            </DialogDescription>
          </DialogHeader>
          
          {/* Resumen */}
          <div className="grid grid-cols-4 gap-4 py-4 border-b">
            <div className="text-center">
              <div className="text-2xl font-bold">{sesionActual?.total_filas_excel}</div>
              <div className="text-xs text-gray-500">Total filas</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{sesionActual?.total_expedientes_encontrados}</div>
              <div className="text-xs text-gray-500">Encontrados</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{sesionActual?.total_expedientes_no_encontrados}</div>
              <div className="text-xs text-gray-500">No encontrados</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{sesionActual?.total_aplicados}</div>
              <div className="text-xs text-gray-500">Aplicados</div>
            </div>
          </div>
          
          {/* Filtros y acciones */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <Select value={filtroItems} onValueChange={setFiltroItems}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filtrar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="encontrados">Encontrados</SelectItem>
                  <SelectItem value="no_encontrados">No encontrados</SelectItem>
                  <SelectItem value="con_diferencias">Con diferencias</SelectItem>
                  <SelectItem value="pendientes">Pendientes</SelectItem>
                  <SelectItem value="aplicados">Aplicados</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="sm" onClick={selectAllVisible}>
                Seleccionar todos
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAll}>
                Deseleccionar
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                {itemsSeleccionados.length} seleccionados
              </span>
              <Button 
                onClick={handleAplicarCambios}
                disabled={itemsSeleccionados.length === 0 || aplicando}
                className="gap-2"
              >
                {aplicando ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <FileCheck className="h-4 w-4" />
                )}
                Aplicar Cambios
              </Button>
            </div>
          </div>
          
          {/* Lista de items */}
          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="p-2 text-left w-10"></th>
                  <th className="p-2 text-left">Fila</th>
                  <th className="p-2 text-left">Caso</th>
                  <th className="p-2 text-left">Cliente</th>
                  <th className="p-2 text-left">Pagado</th>
                  <th className="p-2 text-left">Pendiente</th>
                  <th className="p-2 text-left">Detalle Pagos</th>
                  <th className="p-2 text-left">Estado</th>
                  <th className="p-2 text-left">Expediente</th>
                </tr>
              </thead>
              <tbody>
                {getItemsFiltrados().map(item => (
                  <tr 
                    key={item.id} 
                    className={`border-b hover:bg-gray-50 ${
                      !item.expediente_id ? 'bg-red-50' : 
                      item.pagos_excel?.total_pendiente > 0 ? 'bg-orange-50' : ''
                    }`}
                  >
                    <td className="p-2">
                      {item.expediente_id && item.estado === 'pendiente' && (
                        <Checkbox
                          checked={itemsSeleccionados.includes(item.id)}
                          onCheckedChange={() => toggleItemSelection(item.id)}
                        />
                      )}
                    </td>
                    <td className="p-2 text-gray-500">{item.fila_excel}</td>
                    <td className="p-2 font-mono text-xs">{item.numero_caso_excel || '-'}</td>
                    <td className="p-2 max-w-xs truncate" title={item.cliente_excel}>
                      {item.cliente_excel || '-'}
                    </td>
                    <td className="p-2">
                      {item.pagos_excel?.total_pagado > 0 ? (
                        <span className="text-green-600 font-medium">
                          {formatCurrency(item.pagos_excel.total_pagado)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="p-2">
                      {item.pagos_excel?.total_pendiente > 0 ? (
                        <span className="text-red-600 font-bold">
                          {formatCurrency(item.pagos_excel.total_pendiente)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {item.pagos_excel?.pendientes?.slice(0, 3).map((p, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                            {p.concepto}: {formatCurrency(p.importe)}
                          </Badge>
                        ))}
                        {item.pagos_excel?.pendientes?.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{item.pagos_excel.pendientes.length - 3} más
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-2">
                      <Badge className={ITEM_ESTADO_BADGES[item.estado]?.color || 'bg-gray-100'}>
                        {ITEM_ESTADO_BADGES[item.estado]?.label || item.estado}
                      </Badge>
                    </td>
                    <td className="p-2">
                      {item.expediente_id ? (
                        <Button 
                          variant="link" 
                          size="sm"
                          className="p-0 h-auto"
                          onClick={() => {
                            setShowSesionModal(false);
                            navigate(`/expedientes/${item.expediente_id}`);
                          }}
                        >
                          Ver expediente
                        </Button>
                      ) : (
                        <span className="text-red-500 text-xs">No encontrado</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSesionModal(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
